import {
  OperationLog,
  StorageEvent,
  StorageEventHandler,
  StorageError,
} from "./types";
import { logger } from "../../utils/logger";

/**
 * Escape a value for safe CSV output.
 * Wraps in quotes and escapes embedded quotes if the value contains
 * commas, quotes, or newlines.
 */
function escapeCSVField(value: string | number): string {
  const str = String(value);
  if (
    str.includes(",") ||
    str.includes('"') ||
    str.includes("\n") ||
    str.includes("\r")
  ) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Storage operation logging system
 */
export class LoggingSystem {
  private logs: OperationLog[] = [];
  private eventHandlers: Set<StorageEventHandler> = new Set();
  private readonly MAX_LOGS = 1000; // Keep last 1000 operations

  /**
   * Log a storage operation
   */
  logOperation(
    operation: string,
    key: string,
    startTime: number,
    status: "success" | "failure" = "success",
    error?: StorageError,
    metadata?: Record<string, unknown>,
  ): void {
    const endTime = Date.now();
    const duration = endTime - startTime;

    const log: OperationLog = {
      timestamp: startTime,
      operation,
      key,
      status,
      duration,
      error,
      metadata,
    };

    // Add to logs
    this.logs.push(log);
    this.trimLogs();

    // Emit event
    this.emitEvent({
      type: status === "success" ? "operation" : "error",
      timestamp: endTime,
      data: log,
    });

    // Log to system logger
    if (status === "success") {
      logger.info("Storage operation completed", {
        data: {
          operation,
          key,
          duration,
          metadata,
        },
      });
    } else if (error?.code === "STORAGE_NOT_AVAILABLE") {
      // "Data not found" is expected when tiered storage falls through
      // (e.g., localStorage miss before IndexedDB hit). Not a real error.
      logger.debug("Storage miss (will try next tier)", {
        data: {
          operation,
          key,
          duration,
          metadata,
        },
      });
    } else {
      logger.error("Storage operation failed", {
        data: {
          operation,
          key,
          duration,
          metadata,
        },
        error: error instanceof Error ? error : new Error(String(error)),
      });
    }
  }

  /**
   * Get recent operation logs
   */
  getRecentLogs(count: number = 10): OperationLog[] {
    return this.logs.slice(-count);
  }

  /**
   * Get logs for a specific key
   */
  getLogsByKey(key: string): OperationLog[] {
    return this.logs.filter((log) => log.key === key);
  }

  /**
   * Get error logs
   */
  getErrorLogs(): OperationLog[] {
    return this.logs.filter((log) => log.status === "failure");
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics(): {
    avgOperationTime: number;
    errorRate: number;
    operationCounts: Record<string, number>;
  } {
    const totalOps = this.logs.length;
    if (totalOps === 0) {
      return {
        avgOperationTime: 0,
        errorRate: 0,
        operationCounts: {},
      };
    }

    // Calculate metrics
    const totalTime = this.logs.reduce((sum, log) => sum + log.duration, 0);
    const errorCount = this.logs.filter(
      (log) => log.status === "failure",
    ).length;

    // Count operations by type
    const operationCounts = this.logs.reduce(
      (counts, log) => {
        counts[log.operation] = (counts[log.operation] || 0) + 1;
        return counts;
      },
      {} as Record<string, number>,
    );

    return {
      avgOperationTime: totalTime / totalOps,
      errorRate: errorCount / totalOps,
      operationCounts,
    };
  }

  /**
   * Subscribe to storage events
   */
  subscribe(handler: StorageEventHandler): () => void {
    this.eventHandlers.add(handler);
    return () => this.eventHandlers.delete(handler);
  }

  /**
   * Clear old logs
   */
  clearOldLogs(olderThan: number): void {
    const cutoff = Date.now() - olderThan;
    this.logs = this.logs.filter((log) => log.timestamp >= cutoff);
  }

  /**
   * Export logs
   */
  exportLogs(format: "json" | "csv" = "json"): string {
    if (format === "csv") {
      return this.exportCSV();
    }
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * Emit storage event
   */
  private emitEvent(event: StorageEvent): void {
    this.eventHandlers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        logger.error("Error in storage event handler", {
          error: error instanceof Error ? error : new Error(String(error)),
        });
      }
    });
  }

  /**
   * Trim logs to maximum size
   */
  private trimLogs(): void {
    if (this.logs.length > this.MAX_LOGS) {
      this.logs = this.logs.slice(-this.MAX_LOGS);
    }
  }

  /**
   * Export logs as CSV
   */
  private exportCSV(): string {
    const headers = [
      "timestamp",
      "operation",
      "key",
      "status",
      "duration",
      "error",
    ];
    const rows = this.logs.map((log) => [
      new Date(log.timestamp).toISOString(),
      log.operation,
      log.key,
      log.status,
      log.duration,
      log.error?.message || "",
    ]);

    return [
      headers.join(","),
      ...rows.map((row) => row.map(escapeCSVField).join(",")),
    ].join("\n");
  }
}

// Export singleton instance
export const loggingSystem = new LoggingSystem();
